import React, { useRef, useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon } from 'lucide-react';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bold') ? 'bg-gray-200 text-[#003456]' : 'text-gray-600'}`}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('italic') ? 'bg-gray-200 text-[#003456]' : 'text-gray-600'}`}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('underline') ? 'bg-gray-200 text-[#003456]' : 'text-gray-600'}`}
        title="Underline"
      >
        <UnderlineIcon className="h-4 w-4" />
      </button>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bulletList') ? 'bg-gray-200 text-[#003456]' : 'text-gray-600'}`}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('orderedList') ? 'bg-gray-200 text-[#003456]' : 'text-gray-600'}`}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </button>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <button
        type="button"
        onClick={addLink}
        className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('link') ? 'bg-gray-200 text-[#003456]' : 'text-gray-600'}`}
        title="Add Link"
      >
        <LinkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder = 'Start typing...' }) => {
  // Track if user is currently typing to avoid fighting with external updates
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  
  const handleUpdate = useCallback(({ editor }) => {
    // Mark that user is typing
    isTypingRef.current = true;
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Reset typing flag after 500ms of no typing
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, 500);
    
    onChange(editor.getHTML());
  }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#003456] underline',
        },
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-3 min-h-[120px] focus:outline-none',
      },
    },
    onUpdate: handleUpdate,
  });

  // Sync external value changes to editor (e.g., AI generation)
  useEffect(() => {
    if (!editor) return;
    
    // Don't update if user is actively typing
    if (isTypingRef.current) return;
    
    const currentContent = editor.getHTML();
    
    // Only update if content is actually different
    // Compare normalized content (strip extra whitespace)
    const normalizedValue = (value || '').trim();
    const normalizedCurrent = (currentContent || '').trim();
    
    if (normalizedValue !== normalizedCurrent && normalizedValue !== '<p></p>') {
      console.log('RichTextEditor: Updating content from external source');
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
      {!value && (
        <style>{`
          .ProseMirror p.is-editor-empty:first-child::before {
            color: #9ca3af;
            content: '${placeholder}';
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
      )}
    </div>
  );
};

export default RichTextEditor;
